import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, AlertTriangle, Users, Bell, Shield, TrendingUp, Building,
  Bug, Cloud, ChevronRight, Lock, CheckCircle, BarChart2, Layers,
  ClipboardCheck, Folder, Calendar, Loader2, AlertCircle, Eye, Radar,
  TrendingDown, Minus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FrameworkReadinessWidget from '@/components/grc/FrameworkReadinessWidget';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Area, AreaChart, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [driftData, setDriftData] = useState(null);
  const [driftPeriod, setDriftPeriod] = useState('30');

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadDrift(); }, [driftPeriod]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/dashboard/executive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load executive dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadDrift = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/dashboard/compliance-drift`, {
        params: { days: parseInt(driftPeriod) },
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriftData(res.data);
    } catch (e) {
      console.error('Failed to load compliance drift');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Loading executive dashboard...</p>
      </div>
    </div>
  );

  if (!data) return null;

  const { grc, vendors, litesabre, amidala } = data;

  const riskDistribution = [
    { name: 'Critical', value: grc.risk_distribution?.critical || 0, color: '#DC2626' },
    { name: 'High', value: grc.risk_distribution?.high || 0, color: '#EA580C' },
    { name: 'Medium', value: grc.risk_distribution?.medium || 0, color: '#D97706' },
    { name: 'Low', value: grc.risk_distribution?.low || 0, color: '#16A34A' },
  ];

  const totalRiskCount = riskDistribution.reduce((s, r) => s + r.value, 0);

  const trendIcon = driftData?.trend > 0
    ? <TrendingUp className="w-4 h-4 text-emerald-500" />
    : driftData?.trend < 0
      ? <TrendingDown className="w-4 h-4 text-red-500" />
      : <Minus className="w-4 h-4 text-muted-foreground" />;

  const trendColor = driftData?.trend > 0 ? 'text-emerald-600' : driftData?.trend < 0 ? 'text-red-600' : 'text-muted-foreground';

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="executive-dashboard">
      <div className="mb-2">
        <h1 className="text-2xl font-bold tracking-tight">Executive Dashboard</h1>
        <p className="text-sm text-muted-foreground">Real-time cross-module overview of your security and compliance posture</p>
      </div>

      {/* Top-level KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          label="Compliance Score"
          value={`${grc.compliance_score}%`}
          icon={Shield}
          color="text-emerald-600"
          bg="bg-emerald-50"
          trend={grc.compliance_score >= 80 ? 'good' : grc.compliance_score >= 60 ? 'warning' : 'bad'}
        />
        <KPICard
          label="Open Risks"
          value={grc.total_risks}
          icon={AlertTriangle}
          color="text-amber-600"
          bg="bg-amber-50"
          sub={`${grc.critical_risks} critical`}
          trend={grc.critical_risks > 0 ? 'bad' : 'good'}
        />
        <KPICard
          label="Total Vendors"
          value={vendors.total_vendors}
          icon={Building}
          color="text-blue-600"
          bg="bg-blue-50"
          sub={`${vendors.high_risk_vendors} high-risk`}
          trend={vendors.high_risk_vendors > 0 ? 'warning' : 'good'}
        />
        <KPICard
          label="Active Alerts"
          value={grc.total_alerts}
          icon={Bell}
          color="text-red-600"
          bg="bg-red-50"
          trend={grc.total_alerts > 5 ? 'bad' : grc.total_alerts > 0 ? 'warning' : 'good'}
        />
      </div>

      {/* Framework Readiness — Evidence Collection */}
      <FrameworkReadinessWidget />

      {/* Compliance Drift Chart */}
      {driftData && (
        <Card data-testid="compliance-drift-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-blue-500" />
                  Compliance Drift
                </CardTitle>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-2xl font-bold">{driftData.current_score}%</span>
                  <span className={`flex items-center gap-1 text-sm font-medium ${trendColor}`}>
                    {trendIcon}
                    {driftData.trend > 0 ? '+' : ''}{driftData.trend}%
                  </span>
                  <span className="text-xs text-muted-foreground">over {driftPeriod} days</span>
                </div>
              </div>
              <Select value={driftPeriod} onValueChange={setDriftPeriod}>
                <SelectTrigger className="w-[130px]" data-testid="drift-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                  <SelectItem value="180">6 Months</SelectItem>
                  <SelectItem value="365">1 Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={driftData.snapshots} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="complianceGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="controlGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => {
                      const dt = new Date(d);
                      return `${dt.getMonth() + 1}/${dt.getDate()}`;
                    }}
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                    formatter={(value, name) => [`${Number(value).toFixed(1)}%`, name]}
                    labelFormatter={(d) => new Date(d).toLocaleDateString()}
                  />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    iconType="circle"
                    wrapperStyle={{ fontSize: 12 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="compliance_score"
                    name="Compliance Score"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fill="url(#complianceGrad)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="control_effectiveness"
                    name="Control Effectiveness"
                    stroke="#10B981"
                    strokeWidth={1.5}
                    fill="url(#controlGrad)"
                    dot={false}
                    activeDot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="policy_compliance"
                    name="Policy Compliance"
                    stroke="#8B5CF6"
                    strokeWidth={1.5}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Control Status Churn (from cascade flips) */}
            {driftData.churn && (
              <div className="mt-4 pt-4 border-t border-border" data-testid="control-churn-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Radar className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-semibold">Control Status Churn</span>
                    <span className="text-xs text-muted-foreground">last {driftPeriod} days</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <ChurnStat
                    label="Total Flips"
                    value={driftData.churn.total_flips}
                    icon={BarChart2}
                    color="text-blue-600"
                    bg="bg-blue-50"
                    testId="churn-total-flips"
                  />
                  <ChurnStat
                    label="→ Implemented"
                    value={driftData.churn.flips_to_implemented}
                    icon={CheckCircle}
                    color="text-emerald-600"
                    bg="bg-emerald-50"
                    testId="churn-to-implemented"
                  />
                  <ChurnStat
                    label="Reverted"
                    value={driftData.churn.flips_to_in_progress}
                    icon={TrendingDown}
                    color="text-red-600"
                    bg="bg-red-50"
                    testId="churn-to-in-progress"
                  />
                  <ChurnStat
                    label="Unstable Controls"
                    value={driftData.churn.unstable_controls}
                    icon={AlertTriangle}
                    color="text-amber-600"
                    bg="bg-amber-50"
                    testId="churn-unstable-count"
                  />
                </div>
                {driftData.churn.top_unstable_controls && driftData.churn.top_unstable_controls.length > 0 ? (
                  <div data-testid="top-unstable-controls">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                      Top Unstable Controls
                    </p>
                    <div className="space-y-1.5">
                      {driftData.churn.top_unstable_controls.map((c) => (
                        <div
                          key={c.control_id}
                          className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-muted/40 hover:bg-muted/70 cursor-pointer transition-colors"
                          onClick={() => navigate(`/grc/controls/${c.control_id}`)}
                          data-testid={`unstable-control-${c.control_code || c.control_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-foreground">
                                {c.control_code || c.control_id.slice(0, 8)}
                              </span>
                              <span className="text-xs text-muted-foreground truncate">
                                {c.control_name}
                              </span>
                            </div>
                            {c.last_reason ? (
                              <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                                {c.last_reason}
                              </p>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge
                              variant="outline"
                              className={
                                c.last_status === 'implemented'
                                  ? 'text-emerald-700 border-emerald-200 bg-emerald-50'
                                  : 'text-red-700 border-red-200 bg-red-50'
                              }
                            >
                              {c.last_status || 'unknown'}
                            </Badge>
                            <span className="text-xs font-semibold text-amber-600">
                              {c.flip_count}x
                            </span>
                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic" data-testid="no-churn-message">
                    No control status flips in this period — compliance posture is stable.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Module Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ModuleSection
          title="GRC Core"
          icon={Shield}
          color="blue"
          navigate={navigate}
          link="/grc/frameworks"
          items={[
            { label: 'Active Policies', value: grc.total_policies, icon: FileText },
            { label: 'Frameworks', value: grc.total_frameworks, icon: Layers },
            { label: 'Controls', value: grc.total_controls, icon: ClipboardCheck },
            { label: 'Documents', value: grc.total_documents, icon: Folder },
            { label: 'Scheduled Audits', value: grc.total_audits, icon: Calendar },
            { label: 'Critical Risks', value: grc.critical_risks, icon: AlertCircle, highlight: grc.critical_risks > 0 },
          ]}
        />

        <ModuleSection
          title="Vendor Risk Management"
          icon={Building}
          color="indigo"
          navigate={navigate}
          link="/grc/vendor-assessment"
          items={[
            { label: 'Total Vendors', value: vendors.total_vendors, icon: Users },
            { label: 'High-Risk Vendors', value: vendors.high_risk_vendors, icon: AlertTriangle, highlight: vendors.high_risk_vendors > 0 },
            { label: 'Pending Assessments', value: vendors.pending_assessments, icon: ClipboardCheck },
            { label: 'Completed', value: vendors.completed_assessments, icon: CheckCircle },
            { label: 'Overdue', value: vendors.overdue_assessments, icon: AlertCircle, highlight: vendors.overdue_assessments > 0 },
          ]}
        />

        <ModuleSection
          title="LiteSabre Scanner"
          icon={Bug}
          color="red"
          navigate={navigate}
          link="/litesabre/dashboard"
          items={[
            { label: 'Total Scans', value: litesabre.total_scans, icon: Eye },
            { label: 'Active Scans', value: litesabre.active_scans, icon: Radar },
            { label: 'Vulnerabilities', value: litesabre.total_vulnerabilities, icon: Bug },
            { label: 'Critical Findings', value: litesabre.critical_findings, icon: AlertCircle, highlight: litesabre.critical_findings > 0 },
            { label: 'High Findings', value: litesabre.high_findings, icon: AlertTriangle, highlight: litesabre.high_findings > 0 },
          ]}
        />

        <ModuleSection
          title="Amidala CSPM"
          icon={Cloud}
          color="purple"
          navigate={navigate}
          link="/amidala/dashboard"
          items={[
            { label: 'Cloud Accounts', value: amidala.total_cloud_accounts, icon: Cloud },
            { label: 'CSPM Scans', value: amidala.cspm_scans, icon: Eye },
            { label: 'Misconfigurations', value: amidala.total_misconfigurations, icon: AlertTriangle, highlight: amidala.total_misconfigurations > 0 },
            { label: 'Security Score', value: amidala.cloud_security_score ? `${amidala.cloud_security_score}%` : 'N/A', icon: Shield },
          ]}
        />
      </div>

      {/* Risk Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Risk Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {totalRiskCount === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <div className="text-center">
                <Shield className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm font-medium">No open risks</p>
                <p className="text-xs text-muted-foreground">Your risk register is clear</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-8">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={riskDistribution.filter(r => r.value > 0)} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                      {riskDistribution.filter(r => r.value > 0).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                {riskDistribution.map(r => (
                  <div key={r.name} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color }} />
                    <div>
                      <p className="text-xs text-muted-foreground">{r.name}</p>
                      <p className="text-lg font-bold">{r.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function ChurnStat({ label, value, icon: Icon, color, bg, testId }) {
  return (
    <div className={`p-3 rounded-lg border border-border ${bg}/50`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value}</p>
        </div>
        <Icon className={`w-5 h-5 ${color} opacity-70`} />
      </div>
    </div>
  );
}

function KPICard({ label, value, icon: Icon, color, bg, sub, trend }) {
  const trendColors = { good: 'border-emerald-200', warning: 'border-amber-200', bad: 'border-red-200' };
  return (
    <Card className={`border-2 ${trendColors[trend] || 'border-transparent'}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
            <p className="text-3xl font-bold">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ModuleSection({ title, icon: Icon, color, items, navigate, link }) {
  const colorMap = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', header: 'bg-blue-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-100', header: 'bg-indigo-600' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100', header: 'bg-red-600' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100', header: 'bg-purple-600' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <Card className={`border ${c.border} overflow-hidden`} data-testid={`module-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`${c.header} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-white" />
          <span className="text-sm font-semibold text-white">{title}</span>
        </div>
        <button onClick={() => navigate(link)} className="text-white/70 hover:text-white transition-colors flex items-center gap-1 text-xs">
          View <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100">
          {items.map((item, i) => {
            const ItemIcon = item.icon;
            return (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-2.5">
                  <ItemIcon className={`w-4 h-4 ${item.highlight ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <span className="text-sm text-muted-foreground">{item.label}</span>
                </div>
                <span className={`text-sm font-semibold ${item.highlight ? 'text-red-600' : ''}`}>{item.value}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default ExecutiveDashboard;
